import React, { useContext, useMemo, useState } from 'react'
import { getLocale, useIntl } from 'umi'
import moment from 'moment'
import { TableColumn } from 'tea-component'
import { commonValueFormatter, formatExeclNormalPercent, formatExeclNum, formatNum, formatPercentNum, getChartDateFormatString, getRatioText, handleDownload, unitConversion } from '@/pages/dashboard/utils/utils'
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble'
import { useWeekDay } from '@/pages/dashboard/utils/customHook'
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context'
import Revenue from '../../../components/Revenue'
import { usePCRevenueColumns } from '../PcRevenue'
import { find } from 'lodash-es'


export const axisLabelFormat = (opt: { xAxis: any[] }, timeType: string) => {
  return opt.xAxis?.map((item: { axisLabel: any }) => {
    return {
      ...item,
      axisLabel: {
        ...item.axisLabel,
        formatter: (value: any) => getChartDateFormatString(timeType, value),
      },
    }
  })
}

function PcPayerAndPayer(props: any) {
  const contextObj = useContext(FilterContext)
  const { data: defaultData = [], enName, cnName, defaultActive = 1, config: configs } = props
  const { filterVal: formValue } = contextObj || {};
  const { componentMetricsConfig } = configs
  const { data: dataSource, loading, errorMsg } = defaultData?.PcMonetizationRevenueData || {}
  const data = dataSource?.mtx_payer_and_rate

  const { locale } = useIntl()
  const isEn = locale === "en-US"
  const { timeType = 'Day' } = formValue
  const {
    tableOption,
    chart,
    tableData,
    downloadData
  } = usePCRevenueColumns(data, componentMetricsConfig, timeType)
  const payerAndPayerData = useMemo(() => {
    return data ?? []
  }, [data])


  // 双轴自定义配置
  const config = useMemo(() => {

    const yAxis = [
      {
        type: 'value',
        position: 'left',
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#ddd',
          },
        },
        axisLabel: {
          color: '#888888',
          fontSize: 12,
          formatter: (value: string | number) => unitConversion(value),
        },
      },
      {
        type: 'value',
        position: 'right',
        alignTicks: true, // 多Y轴时，开启是否对齐刻度线
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#ddd',
          },
        },
        axisLabel: {
          formatter: (value: number) => {
            const num = value < 0.01 ? 2 : 0
            return (value && `${formatPercentNum(value, num)}`) || value
          },
        },
      },
    ]
    return {
      bar: {
        formatterFunc: (params: any) => formatterFunc(params, timeType, isEn, componentMetricsConfig,),
        setOtherConfigFunc: (opt: {
          xAxis: any; series: any[]; yAxis: ({ type: string; position: string; splitLine: { show: boolean; lineStyle: { type: string; color: string } }; axisLabel: { color: string; fontSize: number; formatter: (value: any) => string }; alignTicks?: undefined } | {
            type: string; position: string; alignTicks: boolean // 多Y轴时，开启是否对齐刻度线
            splitLine: { show: boolean; lineStyle: { type: string; color: string } }; axisLabel: { formatter: (value: any) => any; color?: undefined; fontSize?: undefined }
          })[]
        }) => {
          opt.xAxis = axisLabelFormat(opt, timeType)
          opt.series = opt.series?.map((item: any, index: number) => {
            return {
              ...item,
              type: index === 0 ? 'bar' : 'line',
              yAxisIndex: index,
            }
          })
          opt.yAxis = yAxis
          return opt
        },
      },
      line: {
        formatterFunc: (params: any) => formatterFunc(params, timeType, isEn, componentMetricsConfig),
        setOtherConfigFunc: (opt: {
          xAxis: any; series: any[]; yAxis: ({ type: string; position: string; splitLine: { show: boolean; lineStyle: { type: string; color: string } }; axisLabel: { color: string; fontSize: number; formatter: (value: any) => string }; alignTicks?: undefined } | {
            type: string; position: string; alignTicks: boolean // 多Y轴时，开启是否对齐刻度线
            splitLine: { show: boolean; lineStyle: { type: string; color: string } }; axisLabel: { formatter: (value: any) => any; color?: undefined; fontSize?: undefined }
          })[]
        }) => {
          opt.xAxis = axisLabelFormat(opt, timeType)
          opt.series = opt.series?.map((item: any, index: any) => {
            return {
              ...item,
              yAxisIndex: index,
            }
          })
          opt.yAxis = yAxis
          return opt
        },
      },
    }
  }, [payerAndPayerData, getLocale()])

  // 折线图 && 表格
  const options = useMemo(() => {
    return {
      chart,
      table: {
        option: tableOption,
        data: tableData,
        downloadData
      },
    }
  }, [payerAndPayerData, getLocale()])


  return (
    <Revenue {...{
      otherOption: options,
      data: {
        MonetizationRevenueData: {
          data: {
            revenue: payerAndPayerData
          },
          loading,
          errorMsg
        }
      },
      enName,
      cnName,
      renderFormatterFunc: (params: any) => {
        const result = formatNum(params?.value, false, true)
        return result === '0.00' ? 0 : result
      },
      otherConfig: config,
      defaultActive,
      isShowLegend: true
    }} />
  )
}

export default PcPayerAndPayer

// 柱状图，线形图 浮窗
export const formatterFunc = (params: { seriesName: any; value: any; color: any }[] | { axisValue?: any }[], timeType: string, isEn: boolean, componentMetricsConfig?: any[]) => {
  return getEchartTooltipText({
    title: moment(`${params[0]?.axisValue}`).format(timeType === 'Month' ? 'YYYY-MM' : 'YYYY-MM-DD'),
    data: params.map(({ seriesName, value, color }: any) => {
      const metric = find(componentMetricsConfig, {
        [isEn ? 'enName' : 'cnName'
        ]: seriesName
      })
      return {
        name: seriesName,
        // TODO:这里为啥要特殊处理，图表的和表格的数据格式居然不一致，待讨论
        metricsNum: commonValueFormatter(metric?.isPercent ? metric?.type : 'int', value, metric?.decimal),
        color,
      }
    }),
  })
}
